import { useMutation } from 'react-query';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { LOCAL_STORAGE_LAST_RESET_PASSWORD_TIMESTAMP } from 'services/api/constants';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset } from 'services/AuthService';
import { toast } from 'react-toastify';
import { LogyxError, getToastErrorMessage } from 'services/api/errors';

export const useRequestPasswordReset = () => {
  const { t } = useTranslation();

  const mutation = useMutation((email: string) => requestPasswordReset(email), {
    onSuccess: () => {
      toast.success(t('Password reset email sent'));
      localStorage.setItem(
        LOCAL_STORAGE_LAST_RESET_PASSWORD_TIMESTAMP,
        Date.now().toString()
      );
    },
    onError: (error: LogyxError) => {
      toast.error(getToastErrorMessage(error));
    },
    mutationKey: ReactMutationKeys.REQUEST_PASSWORD_RESET,
  });

  const sendRequest = (email: string) => {
    const now = Date.now();
    const lastRequestTime = localStorage.getItem(
      LOCAL_STORAGE_LAST_RESET_PASSWORD_TIMESTAMP
    );
    const timeElapsed = lastRequestTime
      ? now - parseInt(lastRequestTime)
      : Infinity;

    if (
      !lastRequestTime ||
      timeElapsed >= 60000 ||
      isNaN(parseInt(lastRequestTime))
    )
      // If last request time is not set or more than one minute has passed
      mutation.mutate(email);
    else
      toast.info(
        `${t(
          'A password reset request has already been sent. Please try again in one minute'
        )}.`
      );
  };

  return {
    mutate: sendRequest,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
  };
};
